import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Button, ButtonContainer, Container, DataGrid, Divider, GridItem, GridRow, Icon, IconButton, Link, SearchField, Stack, Spacer, Typography } from '@emburse/embark-core';
import { AddIcon, MoreVertIcon } from '@emburse/embark-icons';
import { defaults as _defaults } from 'lodash';
import UploadDownload from '../../../../views/QuickSetup/components/UploadDownload';
import DataGridLoading from '../../../../views/QuickSetup/components/DataGridLoading';
// eslint-disable-next-line import/no-cycle
import UsersDrawer from './UsersDrawer';
import { EmpType } from '../../../../interfaces/Employee';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { generateRandomGUID } from '../../../../util/helpers';
import { defaultValidation, validateEmail } from '../../../../util/formValidation';
const defaultUserData = {
    Email: '',
    FirstName: '',
    LastName: '',
    EmpID: '',
    EmpType: '',
    DeptName: '',
    DeptCode: '',
    ApproverEmail: '',
    AccountantEmail: '',
    CurrencyType: '',
    MultipleCurrencies: false,
    UseVAT: false,
    DefaultVATRate: 0,
    UsePST: false,
    DefaultPSTRate: 0,
    UseHST: false,
    DefaultHSTRate: 0
};
export class UserData {
    PK;
    id;
    Email;
    FirstName;
    LastName;
    EmpID;
    EmpType;
    DeptName;
    DeptCode;
    ApproverEmail;
    AccountantEmail;
    CurrencyType;
    MultipleCurrencies;
    UseVAT;
    DefaultVATRate;
    UsePST;
    DefaultPSTRate;
    UseHST;
    DefaultHSTRate;
    constructor(userdata) {
        _defaults(this, userdata, defaultUserData);
    }
}
const empTypes = () => {
    const filtered = Object.values(EmpType).filter((val) => Number.isNaN(Number(val)) && val !== 'None' && val !== 'Guest');
    return filtered.map((item, index) => ({
        name: item,
        value: index + 1
    }));
};
const Users = forwardRef(({ data, loading, upload, download, alert, currencies }, ref) => {
    useTranslation();
    const [tableData, setTableData] = useState([]);
    const [showDrawer, setShowDrawer] = useState(false);
    const [currentRow, setCurrentRow] = useState();
    const [search, setSearch] = useState('');
    useEffect(() => setTableData(data || []), [data]);
    const rowValidation = (row) => {
        const fNameInvalid = defaultValidation(row.FirstName);
        const lNameInvalid = defaultValidation(row.LastName);
        const emailInvalid = validateEmail(row.Email);
        const deptNameInvalid = defaultValidation(row.DeptName);
        const fName = fNameInvalid.length && { field: 'First Name', reason: fNameInvalid };
        const lName = lNameInvalid.length && { field: 'Last Name', reason: lNameInvalid };
        const email = emailInvalid.length && { field: 'Email', reason: emailInvalid };
        const deptName = deptNameInvalid.length && {
            field: 'Department',
            reason: deptNameInvalid
        };
        return [fName, lName, email, deptName];
    };
    useImperativeHandle(ref, () => ({
        getData() {
            return JSON.stringify(tableData);
        },
        isValid() {
            const mappedTable = tableData.map((item, index) => {
                const filteredValidation = rowValidation(item).filter((cell) => !!cell);
                const invalid = filteredValidation.length > 0;
                return (invalid && {
                    row: index + 1,
                    cell: filteredValidation
                });
            });
            return mappedTable.filter((item) => !!item);
        }
    }));
    const filterdTableData = () => tableData.filter((item) => {
        const regex = new RegExp(search, 'gi');
        return (item?.Email.search(regex) >= 0 ||
            item?.FirstName.search(regex) >= 0 ||
            item?.LastName.search(regex) >= 0);
    });
    const userSaveHandler = (user) => {
        // validate user data
        setTableData((prevState) => prevState.map((tRow) => (tRow.id === user.id ? user : tRow)));
        setShowDrawer(false);
        setCurrentRow(null);
    };
    const addRow = () => setTableData((prevState) => [
        new UserData({
            id: generateRandomGUID(),
            CurrencyType: 'USD',
            EmpType: 'Employee'
        }),
        ...prevState
    ]);
    const deleteRow = (id) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        const tableRow = tableDataCopy.findIndex((row) => row.id === id);
        tableDataCopy.splice(tableRow, 1);
        setTableData(tableDataCopy);
    };
    const editRow = (params) => {
        setCurrentRow(params.row);
        setShowDrawer(true);
    };
    const previousClickHandler = () => {
        const index = tableData.findIndex((row) => row.id === currentRow.id);
        if (tableData[index - 1]) {
            setCurrentRow(tableData[index - 1]);
        }
    };
    const nextClickHandler = () => {
        const index = tableData.findIndex((row) => row.id === currentRow.id);
        if (tableData[index + 1]) {
            setCurrentRow(tableData[index + 1]);
        }
    };
    const columns = () => [
        {
            field: 'Email',
            headerName: $t('Email Address'),
            flex: 2,
            editable: true,
            type: 'string'
        },
        {
            field: 'FirstName',
            headerName: $t('First Name'),
            flex: 2,
            editable: true,
            type: 'string'
        },
        {
            field: 'LastName',
            headerName: $t('Last Name'),
            flex: 2,
            editable: true,
            type: 'string'
        },
        {
            field: 'EmpType',
            headerName: $t('Role'),
            flex: 2,
            editable: true,
            type: 'string'
        },
        {
            field: 'DeptName',
            headerName: $t('Department'),
            flex: 2,
            editable: true,
            type: 'string'
        },
        {
            field: 'ApproverEmail',
            headerName: $t('Approver Email'),
            flex: 3,
            editable: true,
            type: 'string'
        },
        {
            field: 'ActionPlaceholder',
            renderHeader: () => _jsx("span", {}),
            width: 55,
            renderCell: (cell) => (_jsx("div", { style: {
                    width: '100%',
                    textAlign: 'center'
                }, children: _jsx(IconButton, { color: "monochrome", size: "medium", title: "Info Button", onClick: () => editRow(cell), children: _jsx(Icon, { iconUrl: MoreVertIcon }) }) }))
        }
    ];
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(UsersDrawer, { currentRow: currentRow, showDrawer: showDrawer, empTypes: empTypes(), onSetShowDrawer: setShowDrawer, onSave: userSaveHandler, onDelete: () => {
                    deleteRow(currentRow.id);
                    setShowDrawer(false);
                }, onPrevious: previousClickHandler, onNext: nextClickHandler, currencies: currencies }), _jsx(Typography, { variant: "h4", children: $t('Users') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`A Certify account is required in order to create, submit, or review expense reports. 
                Use this screen to invite new users to the system.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t('Certify offers four standard user roles. Each role includes capabilities of the previous role, plus some others:') }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Employees ') }), $t("can create and submit expense reports, and make basic changes to their account's configuration")] }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Managers ') }), $t('can approve expense reports submitted by employees, and submit approved reports for reimbursement')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Executives ') }), $t('have the same permissions as Managers, but can view company wide data while running reports')] }), _jsxs(Typography, { children: ["\u00A0 \u2022", _jsx("strong", { children: $t(' Accountants ') }), $t('are the final step in the approval process and have exclusive access to additional reporting tools')] }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('More information on user roles in Certify can be found '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15460511827725-Expense-User-Role-Overview", target: "_blank", children: $t('here') }), "."] }), _jsx(Spacer, { size: 35 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 35 }), _jsxs(GridRow, { justifyContent: "space-between", children: [_jsx(GridItem, { children: _jsx(Typography, { variant: "h5", children: $t('Users') }) }), _jsx(GridItem, { children: _jsx(UploadDownload, { upload: upload, download: download }) })] }), _jsx(Spacer, { size: 15 }), _jsx(GridRow, { children: _jsx(GridItem, { children: _jsx(SearchField, { id: "SearchUsers", label: $t('Search'), doSearchOnChange: true, searchAction: (val) => setSearch(val) }) }) }), _jsx(Spacer, { size: 15 }), alert.show && (_jsx(Alert, { alertTitle: alert.title, severity: alert.severity, action: _jsx(Button, { color: alert.severity, onClick: alert.onClick, children: $t('See All Errors') }), children: alert.text })), !!tableData.length && (_jsx(ButtonContainer, { alignButtons: "right", sx: { position: 'relative' }, children: _jsx(Button, { variant: "text", size: "small", onClick: addRow, endIcon: _jsx(Icon, { iconUrl: AddIcon }), style: {
                        position: 'absolute',
                        zIndex: 99,
                        right: 0,
                        top: '12px'
                    }, children: $t('Add Row') }) })), _jsx(DataGrid, { autoHeight: true, pageSizeOptions: [5, 10, 100], localeText: { noRowsLabel: '' }, rows: filterdTableData(), columns: columns() }), !tableData.length && !loading && (_jsxs(Stack, { spacing: 2, direction: "column", justifyContent: "space-between", sx: {
                    marginTop: '-72px',
                    zIndex: 99,
                    position: 'relative'
                }, children: [_jsx(Button, { size: "medium", onClick: addRow, children: $t('Enter Data Manually') }), _jsx(Typography, { variant: "body2", children: $t('You will need to add ALL data manually') })] })), loading && _jsx(DataGridLoading, {}), !!tableData.length && !loading && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 35 }), _jsxs(Typography, { variant: "body2", children: [$t("We created the first user for you, add as many as you need. Please note that each users' email notifications will be enabled by default. If you wish to manually disable email notifications during testing, you may do so by "), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15457999530637-Switching-To-Another-User", target: "_blank", children: $t('switching to the user') }), $t(' and managing their '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15458269056909-Managing-Email-Notifications", target: "_blank", children: $t('notification settings') }), "."] })] }))] }));
});
export default Users;
